// The Connect a machine seam (`ConnectMachineModal` and the donate wizard's
// enroll body), pure. Two things live here:
//
// `buildConnectCommand` renders the join command the token transport shows, from
// the domain's ONE spelling (`renderBoxJoinCommand`, `@developerz/domain`), with
// the coding model the person typed. The charset gate is the domain renderer's
// own (`BOX_MODEL_ID_PATTERN`): a valid model is exported as `DZ_MODEL_OVERRIDE`
// instead of prompted, an invalid one leaves the prompt-first form. The minted
// code never rides the command in either form — the prompt reads it into a
// header, and the modal reveals it separately. `joinOneLiner` (`lib/fleet.ts`)
// stays for `OnboardingCommand`, which renders the prompt-first form.
//
// `connectPlan` + `planCopy` are the modal's plan line (owner ruling G15, #6567):
// the server's own pair of numbers (`active_boxes` against
// `enrollment_box_budget`, the Fleet page's `useEntitlements` read).
//   - before   — "X of Y machines on this account. This one makes X+1.": the slot
//                about to be taken, said before anything is minted.
//   - after    — "X of Y machines on this account.": the RE-READ entitlements after
//                a mint (the server already counts the pending code), never the
//                pre-mint count plus one.
//   - at_limit — "At Y, you can't connect another until…": plain copy, not an
//                error. Said before a mint when the plan is already full (a
//                personal account at 0 included), and for a `seat_cap` 402.
//   - org_unfunded — an ORG at a limit of 0, or an `org_unfunded` 402: no
//                sentence of ours, `<OrgUnfundedAlert/>` replaces the line.
//   - unlimited — the plan has no limit, and says so.

import { renderBoxJoinCommand } from '@developerz/domain';
import { t } from '../i18n';
import { type SeatInfo, UNLIMITED_AI_DEVS } from './billing';
import type { EnrollRefusal } from './enroll-refusal';

/**
 * The join command for the Connect a machine token transport. `apiBase` is
 * trailing-slash normalized exactly the way `joinOneLiner` does (the SSH
 * bootstrap + `buildJoinScript` convention) so a `PUBLIC_API_URL` ending in `/`
 * can't yield `//join`. `model` is trimmed; the renderer gates its charset.
 */
export function buildConnectCommand(apiBase: string, model?: string): string {
  return renderBoxJoinCommand(`${apiBase.replace(/\/+$/, '')}/join`, { model: model?.trim() });
}

/** What the modal's plan line says. See the header for each kind. */
export type ConnectPlan =
  | { readonly kind: 'before'; readonly active: number; readonly budget: number }
  | { readonly kind: 'after'; readonly active: number; readonly budget: number }
  | { readonly kind: 'at_limit'; readonly budget: number }
  | { readonly kind: 'unlimited'; readonly active: number }
  | { readonly kind: 'org_unfunded' };

/**
 * The plan line for one moment of the dialog. `minted` is "a code was minted
 * since this dialog opened", so `seat` is already the re-read; `refusal` is the
 * last refused mint, whose `reason` outranks the counts (the server decided).
 */
export function connectPlan(
  seat: SeatInfo,
  state: {
    readonly orgUnfunded: boolean;
    readonly minted: boolean;
    readonly refusal?: EnrollRefusal | null;
  },
): ConnectPlan {
  if (state.orgUnfunded) return { kind: 'org_unfunded' };
  const refused = refusalPlan(state.refusal ?? null);
  if (refused !== null) return refused;
  const active = seat.active_boxes;
  const budget = seat.entitlements.enrollment_box_budget;
  if (budget === UNLIMITED_AI_DEVS) return { kind: 'unlimited', active };
  if (state.minted) return { kind: 'after', active, budget };
  if (active >= budget) return { kind: 'at_limit', budget };
  return { kind: 'before', active, budget };
}

/**
 * The plan line a refused mint decides on its own, or `null` for a refusal that
 * is not about the plan (the server's own line, which the transport renders).
 * The dialog uses it with no entitlements read too: a 402 needs no seat to say.
 */
export function refusalPlan(refusal: EnrollRefusal | null): ConnectPlan | null {
  if (refusal?.kind === 'org_unfunded') return { kind: 'org_unfunded' };
  if (refusal?.kind === 'seat_cap') return { kind: 'at_limit', budget: refusal.budget };
  return null;
}

/** The sentence a plan line reads, or `null` where `<OrgUnfundedAlert/>` stands instead. */
export function planCopy(plan: ConnectPlan): string | null {
  switch (plan.kind) {
    case 'before':
      return t('fleet.connect.plan_before', {
        active: plan.active,
        budget: plan.budget,
        next: plan.active + 1,
      });
    case 'after':
      return t('fleet.connect.plan_after', { active: plan.active, budget: plan.budget });
    case 'at_limit':
      return t('fleet.connect.plan_at_limit', { budget: plan.budget });
    case 'unlimited':
      return t('fleet.connect.plan_unlimited', { active: plan.active });
    case 'org_unfunded':
      return null;
  }
}
